/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { KetchConfig } from './types';

/** CDN that serves `boot.js`, experience config, GVL, and telemetry. */
export const KETCH_CDN_ORIGIN = 'https://global.ketchcdn.com';

/** Host for the Ketch SDK runtime (`ketch-sdk.js`) loaded by boot.js. */
export const KETCH_SCRIPT_HOST = 'https://cdn.ketchjs.com';

/** Purpose codes that must not by themselves enable SFCC tracking. */
export const KETCH_ESSENTIAL_PURPOSE_CODES = new Set(['essential_services', 'strictly_necessary', 'necessary']);

const KETCH_CODE_PATTERN = /^[a-zA-Z0-9_-]+$/;

export function isValidKetchCode(value: string | undefined): value is string {
    return typeof value === 'string' && KETCH_CODE_PATTERN.test(value);
}

export function isKetchEnabled(ketch: KetchConfig | undefined): ketch is KetchConfig {
    return Boolean(ketch?.enabled) && isValidKetchCode(ketch?.organization) && isValidKetchCode(ketch?.property);
}

export function getKetchPrivacyPortalOrigin(organization: string): string {
    return `https://${organization}.privacyportal.co`;
}

/**
 * Smart Tag boot URL for the given organization/property.
 * Optionally appends `ketch_lang` (ISO 639-1) so the experience matches the storefront locale.
 */
export function getKetchBootScriptUrl(organization: string, property: string, language?: string): string | undefined {
    if (!isValidKetchCode(organization) || !isValidKetchCode(property)) {
        return undefined;
    }

    const url = `${KETCH_CDN_ORIGIN}/web/v3/config/${organization}/${property}/boot.js`;
    if (!language) {
        return url;
    }

    const lang = language.split('-')[0]?.toLowerCase();
    if (!lang || !/^[a-z]{2}$/.test(lang)) {
        return url;
    }

    return `${url}?ketch_lang=${lang}`;
}

/**
 * Queue-safe stub matching the official Smart Tag snippet. Commands issued before
 * boot.js runs are flushed when the tag initializes.
 */
export function ensureKetchStub(): void {
    if (typeof window === 'undefined') {
        return;
    }

    window.semaphore = window.semaphore || [];
    window.ketch =
        window.ketch ||
        function ketchStub(...args: unknown[]) {
            window.semaphore?.push(args);
        };
}

export function showKetchPreferenceExperience(): void {
    ensureKetchStub();
    window.ketch?.('showPreferenceExperience');
}

export function showKetchConsentExperience(): void {
    ensureKetchStub();
    window.ketch?.('showConsentExperience');
}
